import {
    ReactNode, useCallback, useMemo, useRef,
} from 'react';
import { useParams } from 'react-router-dom';
import {
    query, where, orderBy, limit,
} from 'firebase/firestore';
import { useCurrentUser } from '@rainbow-modules/firebase-hooks';
import {
    InternalTooltip, MenuItem, RenderIf, useDefaultTooltipConnector,
} from 'react-rainbow-components';
import { useOpenModal } from '@rainbow-modules/hooks';
import ButtonIcon from 'components/ButtonIcon';
import PhoneIcon from 'components/icons/phone';
import SmsIcon from 'components/icons/sms';
import ChatIcon from 'components/icons/chat';
import DotsVertical from 'components/icons/dotsVertical';
import NoteIcon from 'components/icons/note';
import OpportunityStuckBadge from 'components/OpportunityStuckBadge';
import { formatRelativeDate } from 'data/services/date/formatter';
import priceFormatter from 'data/services/price/formatter';
import getDisplayName from 'data/services/profile/getDisplayName';
import getInitials from 'data/services/string/getInitials';
import { EntityGet } from 'data/firestore/types';
import { Opportunity } from 'data/firestore/agent/opportunity/types';
import useThreads from 'data/firestore/agent/thread/useCollection';
import useCustomerPanel from 'hooks/useCustomerPanel';
import useSMSChat from 'hooks/useSMSChat';
import useNavigateWithQuery from 'hooks/useNavigateWithQuery';
import useHttpMutation from 'data/firestore/useHttpMutation';
import { OPPORTUNITY_EVENTS } from 'analytics/posthog/types';
import useTrackEvent from 'hooks/useTrackEvent';
import useAgentData from 'hooks/useAgentData';
import { useNexhealthPatientLink } from 'customApps/nexhealth/hooks';
import { CUSTOMER_CALL_MODAL, OPPORTUNITY_DETAILS_DRAWER } from '../../../../../constants';
import getVariantFromScore from '../../../helpers/getVariantFromScore';
import { OpportunityCard as OpportunityCardProps } from '../types';
import {
    CardActions,
    CardBody,
    CardContainer,
    CardFooter,
    CardHeader,
    CardHeaderContent,
    CardTitle,
    CustomerName,
    LastUpdateDateContainer,
    StyledAlertCircle,
    StyledClockIcon,
    StyledBadgeOverlay,
    Score,
    TodoIndicator,
    PriceLabel,
    StyledUserAvatar,
    AvatarFrame,
    NexhealthMark,
    OwnerAvatar,
    StyledListTile,
    StyledActionMenu,
    IndicatorTooltipTrigger,
    UnreadNotesBadgeOverlay,
} from './styled';

type TodoStats = {
    pendingCount?: number;
    doneCount?: number;
    totalCount?: number;
};

type OpportunityCardData = EntityGet<Opportunity> & {
    todoStats?: TodoStats;
    unreadNotes?: {
        count?: number;
        notificationIds?: string[];
    };
};

const IndicatorWithTooltip = ({
    tooltip,
    children,
    onClick,
}: {
    tooltip: string;
    children: ReactNode;
    onClick?: () => void;
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const tooltipRef = useRef();
    const {
        onMouseEnter,
        onMouseLeave,
        isVisible,
    } = useDefaultTooltipConnector({
        tooltipRef,
        triggerRef: () => containerRef as any,
    });

    return (
        <>
            <IndicatorTooltipTrigger
                ref={containerRef}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                onClick={onClick}
                $isClickable={Boolean(onClick)}
            >
                {children}
            </IndicatorTooltipTrigger>
            <InternalTooltip
                triggerElementRef={() => containerRef}
                isVisible={isVisible}
                preferredPosition="top"
                ref={tooltipRef}
            >
                {tooltip}
            </InternalTooltip>
        </>
    );
};

const OpportunityCard = ({
    title,
    ...props
}: OpportunityCardProps) => {
    const data = props as unknown as OpportunityCardData;
    const { agentId = '' } = useParams<{ agentId: string }>();
    const { isAppEnabled } = useAgentData();
    const { uid: userId = '' } = useCurrentUser() || {};
    const { track } = useTrackEvent();
    const {
        id = '', firstName = '', lastName = '', alias = '', displayName: displayNameProp = '', photoURL, channel,
    } = data?.customer || {};
    const { data: linkResult } = useNexhealthPatientLink(
        agentId,
        id,
        isAppEnabled('nexhealth') && Boolean(id),
    );
    const [link] = linkResult?.data || [];
    const isNexhealthLinked = Boolean(link && !link.stale);

    const cardTitle = title || data?.name;

    const [openOpportunityDetailsModal] = useOpenModal(OPPORTUNITY_DETAILS_DRAWER);
    const [openCustomerCallModal] = useOpenModal(CUSTOMER_CALL_MODAL);
    const { openCustomerPanel } = useCustomerPanel();
    const { openChat } = useSMSChat();
    const navigate = useNavigateWithQuery();
    const { mutateAsync: updateUserNotification } = useHttpMutation<{ status: 'read' }, unknown>({
        method: 'PATCH',
    });

    const { data: threads = [] } = useThreads(agentId, {
        listQueryFn: (ref) => query(
            ref,
            where('customer.id', '==', id),
            orderBy('lastMessage.createdAt', 'desc'),
            limit(1),
        ),
        disabled: !agentId || !id,
        track: [agentId, id],
    });

    const [latestThread] = threads || [];

    const handleThreadClick = useCallback(
        () => {
            if (latestThread?.id) {
                track(OPPORTUNITY_EVENTS.BOARD_CONVERSATION_OPENED, {
                    opportunityId: data?.id,
                    threadId: latestThread.id,
                });
                navigate(`/${agentId}/threads/${latestThread.id}`);
            }
        },
        [latestThread, agentId, navigate, track, data?.id],
    );

    const handleSMSClick = useCallback(
        () => {
            track(OPPORTUNITY_EVENTS.BOARD_SMS_SENT, {
                opportunityId: data?.id,
                customerId: id,
            });
            openChat({ ...(data?.customer || {}) });
        },
        [openChat, data, track, id],
    );

    const handleOpenDetails = useCallback(
        () => {
            track(OPPORTUNITY_EVENTS.BOARD_DETAILS_OPENED, {
                opportunityId: data?.id,
                source: 'card_title',
            });
            openOpportunityDetailsModal({ data: data.id });
        },
        [data, openOpportunityDetailsModal, track],
    );

    const handleOpenTodosTab = useCallback(
        () => {
            track(OPPORTUNITY_EVENTS.BOARD_TODOS_VIEWED, { opportunityId: data?.id });
            if (!data?.id) return;
            openOpportunityDetailsModal({
                data: {
                    opportunityId: data.id,
                    activeTab: 'todos',
                },
            });
        },
        [data?.id, openOpportunityDetailsModal],
    );

    const handleOpenNotesTabAndMarkRead = useCallback(
        async () => {
            if (!data?.id) return;

            const notificationIds = data?.unreadNotes?.notificationIds || [];
            if (agentId && userId && notificationIds.length > 0) {
                await Promise.allSettled(
                    notificationIds.map((notificationId) => updateUserNotification({
                        pathname: `/v1/agents/${agentId}/user/${userId}/notifications/${notificationId}`,
                        body: { status: 'read' },
                    })),
                );
            }

            openOpportunityDetailsModal({
                data: {
                    opportunityId: data.id,
                    activeTab: 'notes',
                },
            });
        },
        [
            agentId,
            userId,
            data?.id,
            data?.unreadNotes?.notificationIds,
            openOpportunityDetailsModal,
            updateUserNotification,
        ],
    );

    const handleOpenCustomerPanel = useCallback(
        () => openCustomerPanel({ data: data?.customer }),
        [data, openCustomerPanel],
    );

    const displayName = useMemo(
        () => getDisplayName({
            firstName,
            lastName,
            alias,
            displayName: displayNameProp,
        }),
        [firstName, lastName, alias, displayNameProp],
    );

    const customerInitials = useMemo(() => {
        if (displayName === 'Unknown') return null;
        return getInitials(displayName);
    }, [displayName]);

    const ownerDisplayName = useMemo(() => {
        if (data?.owner) {
            return getDisplayName(data?.owner);
        }
        return null;
    }, [data?.owner]);

    const ownerInitials = useMemo(() => {
        if (ownerDisplayName && ownerDisplayName !== 'Unknown') return getInitials(ownerDisplayName);
        return null;
    }, [ownerDisplayName]);

    const amount = useMemo(
        () => priceFormatter('usd').format((data?.amount || 0) / 100),
        [data?.amount],
    );

    const relativeDateValue = useMemo(
        () => data?.updatedAt && `Updated ${formatRelativeDate(data.updatedAt)}`,
        [data?.updatedAt],
    );

    const isStuck = useMemo(
        () => data?.stuck?.condition === 'stalled',
        [data],
    );

    const todoStats = (data?.todoStats || {}) as TodoStats;
    const pendingTodos = todoStats.pendingCount || 0;
    const doneTodos = todoStats.doneCount || 0;
    const totalTodos = todoStats.totalCount || 0;
    const todoProgressValue = totalTodos > 0
        ? Math.round((doneTodos / totalTodos) * 100)
        : 0;
    const todosTooltipLabel = `${pendingTodos} open TODOs (${totalTodos} total)`;
    const unreadNotesCount = data?.unreadNotes?.count || 0;
    const unreadNotesTooltip = `${unreadNotesCount} unread note${unreadNotesCount === 1 ? '' : 's'}`;
    const canCall = Boolean(data?.customer?.phoneNumber);
    const canOpenConversation = Boolean(latestThread);
    const hasDropdownActions = canCall || canOpenConversation;

    const hasUnseenThreadMessages = canOpenConversation && latestThread?.seenStatus === 'unseen';
    const unreadMessagesTooltip = hasUnseenThreadMessages ? 'Unread messages' : 'No unread messages';

    return (
        <CardContainer isStuck={isStuck}>
            <CardHeader>
                <AvatarFrame>
                    <StyledUserAvatar
                        size="large"
                        initials={customerInitials}
                        src={photoURL}
                        value={id}
                        channel={channel}
                    />
                    <RenderIf isTrue={isNexhealthLinked}>
                        <NexhealthMark title="Linked to NexHealth" />
                    </RenderIf>
                </AvatarFrame>
                <CardHeaderContent>
                    <CustomerName
                        type="button"
                        onClick={handleOpenCustomerPanel}
                    >
                        {displayName}
                    </CustomerName>
                    <CardTitle
                        type="button"
                        onClick={handleOpenDetails}
                    >
                        {cardTitle}
                    </CardTitle>
                </CardHeaderContent>
            </CardHeader>
            <CardBody>
                <LastUpdateDateContainer>
                    <RenderIf isTrue={!isStuck}>
                        <StyledClockIcon />
                    </RenderIf>
                    <RenderIf isTrue={isStuck}>
                        <StyledAlertCircle />
                    </RenderIf>
                    {relativeDateValue}
                    <OpportunityStuckBadge stuck={data?.stuck} />
                </LastUpdateDateContainer>
                <RenderIf isTrue={!!data?.owner}>
                    <StyledListTile
                        icon={(
                            <OwnerAvatar
                                size="x-small"
                                initials={ownerInitials}
                                src={data?.owner?.photoURL}
                                value={data?.owner?.uid}
                            />
                        )}
                        title={`${ownerDisplayName} (Owner)`}
                    />
                </RenderIf>
            </CardBody>
            <CardFooter>
                <PriceLabel>{amount}</PriceLabel>
                <CardActions>
                    <IndicatorWithTooltip tooltip="Score">
                        <Score
                            variant={getVariantFromScore(data?.score)}
                            value={data?.score || 0}
                        />
                    </IndicatorWithTooltip>
                    <RenderIf isTrue={pendingTodos > 0}>
                        <IndicatorWithTooltip
                            tooltip={todosTooltipLabel}
                            onClick={handleOpenTodosTab}
                        >
                            <TodoIndicator
                                value={todoProgressValue}
                                $pendingTodos={pendingTodos}
                            />
                        </IndicatorWithTooltip>
                    </RenderIf>
                    <RenderIf isTrue={unreadNotesCount > 0}>
                        <IndicatorWithTooltip tooltip={unreadNotesTooltip}>
                            <ButtonIcon
                                size="small"
                                icon={(
                                    <UnreadNotesBadgeOverlay
                                        overlap="circle"
                                        value={unreadNotesCount}
                                        position="top-right"
                                    >
                                        <NoteIcon />
                                    </UnreadNotesBadgeOverlay>
                                )}
                                onClick={handleOpenNotesTabAndMarkRead}
                            />
                        </IndicatorWithTooltip>
                    </RenderIf>
                    <RenderIf isTrue={data?.customer?.phoneNumber}>
                        <ButtonIcon
                            size="small"
                            icon={(
                                <StyledBadgeOverlay
                                    overlap="circle"
                                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                    // @ts-ignore
                                    isHidden={!data?.customer?.hasNewMessage}
                                >
                                    <SmsIcon />
                                </StyledBadgeOverlay>
                            )}
                            onClick={handleSMSClick}
                        />
                    </RenderIf>
                    <RenderIf isTrue={hasUnseenThreadMessages}>
                        <IndicatorWithTooltip tooltip={unreadMessagesTooltip}>
                            <ButtonIcon
                                size="small"
                                icon={(
                                    <StyledBadgeOverlay
                                        overlap="circle"
                                        position="top-right"
                                    >
                                        <ChatIcon />
                                    </StyledBadgeOverlay>
                                )}
                                onClick={handleThreadClick}
                            />
                        </IndicatorWithTooltip>
                    </RenderIf>
                    <RenderIf isTrue={hasDropdownActions}>
                        <StyledActionMenu
                            buttonSize="small"
                            buttonVariant="base"
                            menuAlignment="right"
                            icon={<DotsVertical />}
                        >
                            <RenderIf isTrue={canCall}>
                                <MenuItem
                                    label="Call"
                                    icon={<PhoneIcon />}
                                    onClick={
                                        () => openCustomerCallModal({ customer: data?.customer })
                                    }
                                />
                            </RenderIf>
                            <RenderIf isTrue={canOpenConversation}>
                                <MenuItem
                                    label="Conversation"
                                    icon={<ChatIcon />}
                                    onClick={handleThreadClick}
                                />
                            </RenderIf>
                        </StyledActionMenu>
                    </RenderIf>
                </CardActions>
            </CardFooter>
        </CardContainer>
    );
};

export default OpportunityCard;
